import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { createErpSocket } from "../utils/socketIo";
import {
  hasPermission,
  PERMISSIONS,
  ROJMEL_ROUTE_ANY_PERMISSIONS,
} from "../utils/permissions";
import {
  node_imageBaseUrl,
  getUserData,
  getToken,
  encryptData,
  getCurrentPinType,
  setUserPermissions,
  getQuoteInvoiceListScope,
  setQuoteInvoiceListScope,
  QUOTE_INVOICE_LIST_SCOPE_EVENT,
} from "./const";
import {
  getErpAuthState,
  getErpToken,
  getFinancialYear,
  setFinancialYear,
  listFiscalYears,
  applyRealtimeSessionFromAdminUserSocket,
} from "../erpClient";
import { useIdleContext } from "../context/IdleContext";
import { trackEvent, getTenantName } from "../services/analytics";
import ThemeSelector from "../components/ThemeSelector";
import ThemeTintedLogo from "../components/ThemeTintedLogo";
import useTheme from "../hooks/useTheme";
import CashbookQuickPopup from "../components/CashbookQuickPopup";
import SignOutConfirmModal from "../components/SignOutConfirmModal";
import WhatsAppSetupModal from "../components/whatsapp/WhatsAppSetupModal";
import { performAppSignOut } from "../utils/signOut";
import { hasAnyWhatsAppAccess } from "../utils/whatsappPermissions";
import { useWhatsAppGate } from "../hooks/useWhatsAppGate";

// Avatar color variants
const AVATAR_COLORS = [
  "bg-primary",
  "bg-secondary",
  "bg-info",
  "bg-success",
  "bg-warning",
  "bg-danger",
];

const NAVBAR_LOGO_SRC = `${
  process.env.PUBLIC_URL || ""
}/assets/images/logos/sidebaricon.png`;

/** Ledger report pages — paths and permissions aligned with SwitchPage routes. */
const NAV_LEDGER_REPORTS = [
  {
    label: "Rojmel",
    path: "/rojmel",
    permission: ROJMEL_ROUTE_ANY_PERMISSIONS,
    icon: "solar:notebook-minimalistic-line-duotone",
  },
  {
    label: "Overall Ledger",
    path: "/overallledger",
    permission: PERMISSIONS.OVERALL_LEDGER,
    icon: "solar:chart-2-line-duotone",
  },
  {
    label: "Sales Ledger",
    path: "/salesledger",
    permission: PERMISSIONS.SALES_LEDGER,
    icon: "solar:bill-list-linear",
  },
  {
    label: "Purchase Ledger",
    path: "/purchaseledger",
    permission: PERMISSIONS.PURCHASE_LEDGER,
    icon: "solar:bag-check-linear",
  },
  {
    label: "Customer Ledger",
    path: "/customerledger",
    permission: PERMISSIONS.CUSTOMER_LEDGER,
    icon: "solar:users-group-rounded-linear",
  },
  {
    label: "Karigar Ledger",
    path: "/karigarledger",
    permission: PERMISSIONS.KARIGAR_LEDGER,
    icon: "solar:user-hands-linear",
  },
  {
    label: "URD Ledger",
    path: "/urdledger",
    permission: PERMISSIONS.URD_LEDGER,
    icon: "solar:transfer-horizontal-linear",
  },
  {
    label: "Bank Ledger",
    path: "/bankledger",
    permission: [PERMISSIONS.GIRVIBANKLEDGER, PERMISSIONS.BANK_LEDGER],
    icon: "solar:banknote-2-linear",
  },
];

/** Paths allowed under quote-mode list scope "quotation" (navbar Ledgers). */
const QUOTE_SCOPE_QUOTATION_LEDGER_PATHS = new Set([
  "/rojmel",
  "/salesledger",
  "/customerledger",
]);

function canAccessNavPermission(permission) {
  if (Array.isArray(permission)) {
    return permission.some((p) => hasPermission(p));
  }
  return hasPermission(permission);
}

/** Legacy login uses encrypted `userData`; ERP OTP uses plain `user-details` — merge for display. */
const mergeDisplayUser = (legacy, erpUser) => {
  if (!legacy && !erpUser) return null;
  const name =
    (legacy?.name && String(legacy.name).trim()) ||
    (erpUser?.name && String(erpUser.name).trim()) ||
    (erpUser?.full_name && String(erpUser.full_name).trim()) ||
    (erpUser?.email && String(erpUser.email).trim()) ||
    "";
  const phone =
    (legacy?.phone && String(legacy.phone).trim()) ||
    (erpUser?.phone && String(erpUser.phone).trim()) ||
    (erpUser?.mobile && String(erpUser.mobile).trim()) ||
    "";
  return {
    ...(legacy || {}),
    ...(erpUser || {}),
    name,
    phone,
    id: legacy?.id ?? erpUser?.id ?? erpUser?.user_id ?? erpUser?.ua_id,
    ua_id: legacy?.ua_id ?? erpUser?.ua_id ?? erpUser?.id,
  };
};

const displayUserLabel = (u) =>
  (u?.name && String(u.name).trim()) ||
  (u?.phone && String(u.phone).trim()) ||
  (u?.mobile && String(u.mobile).trim()) ||
  "Guest User";

function NavQuickActionButton({ icon, label, onClick, variant = "default" }) {
  const variantClass =
    variant === "cash-in"
      ? "navbar-quick-action-btn--cash-in"
      : variant === "cash-out"
        ? "navbar-quick-action-btn--cash-out"
        : variant === "sales"
          ? "navbar-quick-action-btn--sales"
          : variant === "products"
            ? "navbar-quick-action-btn--products"
            : variant === "huid"
              ? "navbar-quick-action-btn--huid"
              : variant === "daily-price"
                ? "navbar-quick-action-btn--daily-price"
                : "";

  return (
    <button
      type="button"
      className={`btn navbar-quick-action-btn ${variantClass}`.trim()}
      onClick={onClick}
    >
      <iconify-icon icon={icon} className="navbar-quick-action-icon" />
      <span>{label}</span>
    </button>
  );
}

function NavbarQuickActions({
  showCashQuickActions,
  showSalesQuickAction,
  showProductsQuickAction,
  showHuidBarcodeQuickAction,
  showDailyPriceQuickAction,
  visibleLedgerReports,
  onCashIn,
  onCashOut,
  onNavigate,
  salesPath = "/sales",
  isQuoteMode = false,
  quoteListScope = "quotation",
  onQuoteListScopeChange,
}) {
  return (
    <>
      {showCashQuickActions && (
        <div className="d-inline-flex align-items-center flex-wrap gap-1 navbar-cash-actions">
          <NavQuickActionButton
            variant="cash-in"
            icon="solar:card-recive-linear"
            label="Cash In"
            onClick={onCashIn}
          />
          <NavQuickActionButton
            variant="cash-out"
            icon="solar:card-send-linear"
            label="Cash Out"
            onClick={onCashOut}
          />
        </div>
      )}
      {showSalesQuickAction && (
        <NavQuickActionButton
          variant="sales"
          icon="solar:cart-3-line-duotone"
          label="Sales"
          onClick={() => onNavigate(salesPath)}
        />
      )}
      {showProductsQuickAction && (
        <NavQuickActionButton
          variant="products"
          icon="solar:clipboard-list-line-duotone"
          label="Products"
          onClick={() => onNavigate("/stocktable")}
        />
      )}
      {showHuidBarcodeQuickAction && !isQuoteMode && (
        <NavQuickActionButton
          variant="huid"
          icon="solar:magnifer-linear"
          label="HUID/Barcode"
          onClick={() => onNavigate("/huidbarcodesearch")}
        />
      )}
      {showDailyPriceQuickAction && (
        <NavQuickActionButton
          variant="daily-price"
          icon="solar:chart-2-line-duotone"
          label="Daily Price"
          onClick={() => onNavigate("/dailyprice")}
        />
      )}
      <NavReportsDropdown
        items={visibleLedgerReports}
        onNavigate={onNavigate}
      />
      {isQuoteMode && (
        <div
          className="navbar-quote-scope-switch"
          data-scope={quoteListScope === "all" ? "all" : "quotation"}
          role="group"
          aria-label="Quotation list scope"
        >
          <span className="navbar-quote-scope-thumb" aria-hidden="true" />
          <button
            type="button"
            className={`navbar-quote-scope-option${
              quoteListScope === "quotation" ? " is-active" : ""
            }`}
            aria-pressed={quoteListScope === "quotation"}
            onClick={() => onQuoteListScopeChange?.("quotation")}
          >
            Quotation
          </button>
          <button
            type="button"
            className={`navbar-quote-scope-option${
              quoteListScope === "all" ? " is-active" : ""
            }`}
            aria-pressed={quoteListScope === "all"}
            onClick={() => onQuoteListScopeChange?.("all")}
          >
            All
          </button>
        </div>
      )}
    </>
  );
}

function NavReportsDropdown({ items, onNavigate }) {
  const [open, setOpen] = useState(false);
  const leaveTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    },
    [],
  );

  if (!items.length) return null;

  const clearLeaveTimer = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  const handleEnter = () => {
    clearLeaveTimer();
    setOpen(true);
  };

  const handleLeave = () => {
    clearLeaveTimer();
    leaveTimerRef.current = setTimeout(() => setOpen(false), 150);
  };

  const handleSelect = (path) => {
    clearLeaveTimer();
    setOpen(false);
    onNavigate(path);
  };

  return (
    <div
      className={`dropdown navbar-reports-dropdown${open ? " is-open" : ""}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        className={`btn navbar-quick-action-btn navbar-quick-action-btn--ledgers navbar-reports-trigger dropdown-toggle ${
          open ? "show" : ""
        }`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <iconify-icon
          icon="solar:chart-2-line-duotone"
          className="navbar-quick-action-icon"
        />
        <span>Ledgers</span>
        <iconify-icon
          icon="solar:alt-arrow-down-linear"
          className="navbar-reports-chevron"
          aria-hidden
        />
      </button>
      <ul
        className={`dropdown-menu navbar-reports-menu shadow-sm ${
          open ? "show" : ""
        }`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {items.map((item) => (
          <li key={item.path}>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => handleSelect(item.path)}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Navbar({
  toggleSidebar,
  layoutType,
  currentModule = "Home",
  showActionControls = true,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setupOpen: whatsAppSetupOpen,
    initialSettings: whatsAppInitialSettings,
    checking: whatsAppChecking,
    openSetupIfNeeded,
    handleSetupSuccess: handleWhatsAppSetupSuccess,
    handleSetupCancel: handleWhatsAppSetupCancel,
  } = useWhatsAppGate(navigate);
  const isDihranModule =
    currentModule === "Dihran" ||
    /\/girvi/i.test(location.pathname) ||
    location.pathname.startsWith("/addgirvi");
  const isEmployeeSelfServicePage =
    location.pathname === "/employeeselfservice" ||
    location.pathname.startsWith("/employeeselfservice/");
  const isWhatsAppPage = location.pathname.startsWith("/whatsapp");
  const [userData, setUserData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const { lockApp } = useIdleContext();
  const { currentTheme } = useTheme();
  const isPremiumTheme = currentTheme === "premium";
  const [currentPinType, setCurrentPinType] = useState(getCurrentPinType());
  const [quoteListScope, setQuoteListScope] = useState(getQuoteInvoiceListScope);
  const [socket, setSocket] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(
    () => getFinancialYear() || "",
  );
  const [isFiscalYearLoading, setIsFiscalYearLoading] = useState(false);
  const [showCashPopup, setShowCashPopup] = useState(false);
  const [cashFlowType, setCashFlowType] = useState("cash-in");
  const showSidebar = layoutType === "sidebar";

  const showWalkins =
    hasPermission(PERMISSIONS.CUSTOMER_ARRIVAL) ||
    hasPermission(PERMISSIONS.CUSTOMER_ASSIGN) ||
    hasPermission(PERMISSIONS.CUSTOMER);
  const showOrders =
    hasPermission(PERMISSIONS.SALES) ||
    hasPermission(PERMISSIONS.INVOICE) ||
    hasPermission(PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE) ||
    hasPermission(PERMISSIONS.CUSTOM_ORDER_ENTRY);
  const showParty =
    hasPermission(PERMISSIONS.CUSTOMER) || hasPermission(PERMISSIONS.SUPPLIER);
  const showItemGroups =
    hasPermission(PERMISSIONS.METAL_GROUP) ||
    hasPermission(PERMISSIONS.ITEM_CATEGORY) ||
    hasPermission(PERMISSIONS.PRODUCT_GROUP) ||
    hasPermission(PERMISSIONS.PRODUCT_ASSORTING);

  const showCashQuickActions =
    hasPermission(PERMISSIONS.CUSTOMER) ||
    hasPermission(PERMISSIONS.SUPPLIER) ||
    hasPermission(PERMISSIONS.CASHBOOK_SETTLEMENT);

  const showSalesQuickAction =
    hasPermission(PERMISSIONS.SALES) || hasPermission(PERMISSIONS.INVOICE);

  const showProductsQuickAction = hasPermission(PERMISSIONS.STOCK_TABLE);

  const showHuidBarcodeQuickAction = hasPermission(PERMISSIONS.STOCK_TABLE);

  const showDailyPriceQuickAction = hasPermission(PERMISSIONS.DAILY_PRICE);

  const showEmployeeSelfServiceNav = true;
  const showWhatsAppNav = hasAnyWhatsAppAccess();

  const visibleLedgerReports = useMemo(() => {
    const permitted = NAV_LEDGER_REPORTS.filter((item) =>
      canAccessNavPermission(item.permission),
    );
    // Quote PIN + Quotation scope: only Rojmel, Sales, Customer ledgers.
    // Quote PIN + All (or access mode): full permitted list.
    if (
      currentPinType === "quote" &&
      quoteListScope !== "all"
    ) {
      return permitted.filter((item) =>
        QUOTE_SCOPE_QUOTATION_LEDGER_PATHS.has(item.path),
      );
    }
    return permitted;
  }, [forceUpdate, currentPinType, quoteListScope]);

  const showNavbarQuickActions =
    showCashQuickActions ||
    showSalesQuickAction ||
    showProductsQuickAction ||
    showHuidBarcodeQuickAction ||
    showDailyPriceQuickAction ||
    visibleLedgerReports.length > 0 ||
    currentPinType === "quote";

  const openCashPopup = (type) => {
    setCashFlowType(type);
    setShowCashPopup(true);
  };

  const closeCashPopup = () => {
    setShowCashPopup(false);
  };

  // Helper function to get user initials
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  // Helper function to get avatar color
  const getAvatarColor = (name) => {
    const sum = name
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return AVATAR_COLORS[sum % AVATAR_COLORS.length];
  };

  // Function to load and update profile image (legacy userData + ERP session user)
  const loadProfileImage = () => {
    const legacy = getUserData();
    const erpUser = getErpAuthState().user;
    const merged = mergeDisplayUser(legacy, erpUser);
    if (merged) {
      setUserData(merged);
      if (merged.img) {
        const imagePath = node_imageBaseUrl + merged.img;
        setProfileImage(imagePath);
      } else {
        setProfileImage(null);
      }
    } else {
      setUserData(null);
      setProfileImage(null);
    }
  };

  useEffect(() => {
    // Load profile image on mount
    loadProfileImage();

    // Listen for storage changes (when userData is updated in other components)
    const handleStorageChange = (e) => {
      if (
        e.key === "userData" ||
        e.key === "user-details" ||
        e.key === "api-token" ||
        e.key === null
      ) {
        loadProfileImage();
      }
    };

    // Listen for custom event that can be dispatched from MyProfile
    const handleProfileUpdate = () => {
      loadProfileImage();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("profileImageUpdated", handleProfileUpdate);
    window.addEventListener("erpAuthUpdated", loadProfileImage);

    // Also refresh when window gains focus (user might have updated profile in another tab)
    const handleFocus = () => {
      loadProfileImage();
    };
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("profileImageUpdated", handleProfileUpdate);
      window.removeEventListener("erpAuthUpdated", loadProfileImage);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadFiscalYears = async () => {
      setIsFiscalYearLoading(true);
      try {
        const response = await listFiscalYears();
        const responseList = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const normalizedYears = responseList
          .map((year) => String(year || "").trim())
          .filter(Boolean);

        if (!isMounted) return;
        setFiscalYears(normalizedYears);

        if (normalizedYears.length === 0) {
          setSelectedFiscalYear("");
          setFinancialYear("");
          return;
        }

        const storedValue = getFinancialYear();
        const resolvedValue = normalizedYears.includes(storedValue)
          ? storedValue
          : normalizedYears[0];
        setSelectedFiscalYear(resolvedValue);
        setFinancialYear(resolvedValue);
      } catch (error) {
        if (!isMounted) return;
        setFiscalYears([]);
      } finally {
        if (isMounted) {
          setIsFiscalYearLoading(false);
        }
      }
    };

    loadFiscalYears();
    return () => {
      isMounted = false;
    };
  }, []);

  const formatFiscalYearShort = (value) => {
    const text = String(value || "").trim();
    const parts = text.split("-");
    if (parts.length === 2) {
      const start = parts[0].trim();
      const end = parts[1].trim();
      const shortStart = start.length >= 2 ? start.slice(-2) : start;
      const shortEnd = end.length >= 2 ? end.slice(-2) : end;
      return `${shortStart}-${shortEnd}`;
    }
    if (/^\d{4}$/.test(text)) return text.slice(-2);
    return text;
  };

  const handleFiscalYearChange = (value) => {
    const valueText = String(value || "").trim();
    if (!valueText) return;
    setSelectedFiscalYear(valueText);
    setFinancialYear(valueText);
  };

  const handleSignOut = () => {
    trackEvent("Authentication", "Logout", getTenantName());
    setShowSignOutModal(true);
  };

  const confirmSignOut = () => {
    trackEvent("Authentication", "Logout", getTenantName());
    setShowSignOutModal(false);
    performAppSignOut(navigate);
  };

  // Listen for API URL changes to update Quote indicator
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      setCurrentPinType(event.detail.pinType);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  // Socket.IO listener for real-time permission updates
  useEffect(() => {
    // Legacy token or ERP Bearer session
    if (!getToken() && !getErpToken()) {
      return;
    }

    const newSocket = createErpSocket();
    setSocket(newSocket);

    newSocket.on("employee_permission_updated", (data) => {
      applyRealtimeSessionFromAdminUserSocket(data);

      if (!data?.employee) return;

      const updatedEmployee = {
        ua_id: data.employee.id || data.employee.ua_id,
        name: data.employee.name || "",
        phone: data.employee.phone || "",
        type: parseInt(data.employee.usertype || data.employee.type) || 0,
        status: parseInt(data.employee.status) || 0,
        email: data.employee.email || "",
      };

      // Check if the updated employee is the currently logged-in user
      if (
        updatedEmployee.ua_id === userData?.id ||
        updatedEmployee.ua_id === userData?.ua_id
      ) {
        // Update logged-in user's data for real-time permission changes
        const updatedUserData = {
          ...userData,
          usertype: updatedEmployee.type,
          status: updatedEmployee.status,
        };

        setUserData(updatedUserData);

        // Also update the persistent storage so changes persist after page refresh
        try {
          // Get current user data and update only permission fields
          const currentUserData = getUserData();
          if (currentUserData) {
            const updatedStorageData = {
              ...currentUserData,
              usertype: updatedEmployee.type,
              status: updatedEmployee.status,
            };

            // Re-encrypt and store using the same method as getUserData/setUserData
            localStorage.setItem("userData", encryptData(updatedStorageData));

            // CRITICAL: Extract and update permissions from socket data
            const permissionFields = [
              "daily_price",
              "bank",
              "supplier",
              "employee",
              "customer_arrival",
              "customer_assign",
              "customer",
              "metal_group",
              "item_category",
              "product_group",
              "stock",
              "stock_table",
              "edit_stock",
              "bill_type",
              "sales",
              "invoice",
              "edit_invoice",
              "karigar",
              "custom_order_entry",
              "custom_order_entry_table",

              "bulk_stock",
              "repairing_order",
              "order_queue",
              "sales_target_view",
              "sales_target_edit",
              "dashboard",
              "purchase_table",
              "add_purchase",
              "edit_purchase",
              "metal_purchase_table",
              "add_old_purchase",
              "edit_old_purchase",
              "edit_custom_order_entry",
              "core_products",
              "product_assorting_table",
              "repairing_order_entry",
              "edit_repairing_order",
              "jangad",
              "add_jangad",
              "edit_jangad",
              "refinery",
              "add_refinery",
              "edit_refinery",
              "add_settlement",
              "overall_ledger",
              "rojmel_view",
              "purchase_ledger",
              "urd_ledger",
              "sales_ledger",
              "karigar_ledger",
              "bank_ledger",
              "customer_ledger",
            ];

            // Extract permissions from socket data
            const updatedPermissions = {};
            permissionFields.forEach((field) => {
              if (data.employee[field] !== undefined) {
                updatedPermissions[field] = data.employee[field];
              }
            });

            // Update permissions in localStorage
            setUserPermissions(updatedPermissions);

            // Dispatch custom event to notify all components to refresh permissions
            window.dispatchEvent(
              new CustomEvent("permissionsUpdated", {
                detail: {
                  usertype: updatedEmployee.type,
                  status: updatedEmployee.status,
                  permissions: updatedPermissions,
                },
              }),
            );
          }
        } catch (error) {
          console.error("Error updating stored user data:", error);
        }

        // Force re-render of navbar to update permissions
        setForceUpdate((prev) => prev + 1);
      }
    });

    return () => {
      newSocket.off("employee_permission_updated");
      newSocket.disconnect();
    };
  }, [userData]);

  // Listen for permission updates from socket or other components
  useEffect(() => {
    const handlePermissionsUpdate = () => {
      const refreshedUserData = mergeDisplayUser(
        getUserData(),
        getErpAuthState().user,
      );
      setUserData(refreshedUserData);
      setForceUpdate((prev) => prev + 1);
    };

    window.addEventListener("permissionsUpdated", handlePermissionsUpdate);
    return () => {
      window.removeEventListener("permissionsUpdated", handlePermissionsUpdate);
    };
  }, []);

  const handleManualLock = () => {
    lockApp("manual");
  };

  const profileDisplayLabel = displayUserLabel(userData);
  const profileAvatarSeed =
    profileDisplayLabel === "Guest User" ? "Guest" : profileDisplayLabel;
  const profileInitials =
    profileDisplayLabel === "Guest User"
      ? "GU"
      : getInitials(profileDisplayLabel);

  return (
    <div>
      <header className="topbar">
        <div className="with-vertical">
          {/* ---------------------------------- */}
          {/* Start Vertical Layout Header */}
          {/* ---------------------------------- */}
          <nav className="navbar navbar-expand-lg p-0 w-100 align-items-center">
            <div className="container-fluid g-2 align-items-center flex-wrap">
              <div className="d-flex align-items-center flex-shrink-0 gap-1 gap-sm-2">
                {(!showSidebar || isPremiumTheme) && (
                  <div className="navbar-brand d-flex align-items-center py-0 m-0">
                    <ThemeTintedLogo
                      src={NAVBAR_LOGO_SRC}
                      alt="Logo"
                      role="button"
                      tabIndex={0}
                      className="topbar-brand-img"
                      sizerClassName="topbar-brand-img"
                      onClick={() => navigate("/dashboard")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate("/dashboard");
                        }
                      }}
                    />
                  </div>
                )}
                {showSidebar && !isPremiumTheme && (
                  <button
                    type="button"
                    className="nav-link sidebartoggler nav-icon-hover-bg rounded-circle border-0 bg-transparent d-flex align-items-center justify-content-center"
                    id="headerCollapse"
                    onClick={toggleSidebar}
                    aria-label="Toggle sidebar"
                  >
                    <iconify-icon
                      icon="solar:hamburger-menu-line-duotone"
                      className="fs-6"
                    />
                  </button>
                )}
                {currentPinType === "quote" && (
                  <span className="text-muted fw-bold user-select-none d-none d-sm-inline fs-5 text-uppercase navbar-quote-label">
                    Quotation
                  </span>
                )}
                {currentPinType === "quote" && (
                  <span className="text-muted fw-bold user-select-none d-inline d-sm-none small text-uppercase navbar-quote-label">
                    Quote
                  </span>
                )}
              </div>

              {!isDihranModule && (
                <button
                  type="button"
                  className="navbar-toggler d-lg-none ms-auto p-0 border-0 nav-icon-hover-bg rounded-circle flex-shrink-0"
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarNav"
                  aria-controls="navbarNav"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
                >
                  <iconify-icon
                    icon="solar:menu-dots-bold-duotone"
                    className="fs-6"
                  />
                </button>
              )}

              <div
                className={`collapse navbar-collapse flex-grow-1 ${
                  isDihranModule ? "d-none" : ""
                }`}
                id="navbarNav"
              >
                <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 gap-2 gap-lg-3 py-2 py-lg-0">
                  {showSidebar && showNavbarQuickActions && !isDihranModule && (
                    <div className="d-flex flex-wrap align-items-center gap-2 navbar-quick-actions w-100 w-lg-auto">
                      <NavbarQuickActions
                        showCashQuickActions={showCashQuickActions}
                        showSalesQuickAction={showSalesQuickAction}
                        showProductsQuickAction={showProductsQuickAction}
                        showHuidBarcodeQuickAction={showHuidBarcodeQuickAction}
                        showDailyPriceQuickAction={showDailyPriceQuickAction}
                        visibleLedgerReports={visibleLedgerReports}
                        onCashIn={() => openCashPopup("cash-in")}
                        onCashOut={() => openCashPopup("cash-out")}
                        onNavigate={navigate}
                        salesPath={
                          currentPinType === "quote" ? "/invoice" : "/sales"
                        }
                        isQuoteMode={currentPinType === "quote"}
                        quoteListScope={quoteListScope}
                        onQuoteListScopeChange={(scope) => {
                          setQuoteListScope(scope);
                          setQuoteInvoiceListScope(scope);
                        }}
                      />
                    </div>
                  )}
                  <div className="d-flex align-items-center justify-content-between justify-content-lg-end flex-grow-1 ms-lg-auto min-w-0">
                    <style>{`
                  .hover-dropdowns .dropdown:hover .dropdown-menu {display:block;}
                  .hover-dropdowns .dropdown-menu { 
                    margin-top: 0.125rem !important;
                    min-width: 8rem !important;
                  }
                  .hover-dropdowns .dropdown-menu .dropdown-item { 
                    color: #212529 !important; 
                    font-weight: 500 !important;
                  }
                  .hover-dropdowns .dropdown-menu .dropdown-item:hover { 
                    color: #212529 !important; 
                    background-color: #f8f9fa !important; 
                  }
                  .hover-dropdowns .dropdown-menu .dropdown-item:focus,
                  .hover-dropdowns .dropdown-menu .dropdown-item:active {
                    font-weight: 500 !important;
                    color: #212529 !important;
                    background-color: #f8f9fa !important;
                    outline: none !important;
                    box-shadow: none !important;
                    border: none !important;
                  }
                  .hover-dropdowns .dropdown-menu .dropdown-item.active {
                    color: #fff !important;
                    background-color: var(--theme-primary) !important;
                    font-weight: 600 !important;
                  }
                  .hover-dropdowns .menu-link { font-weight: 700; color: #29343D !important; font-size: 16.5px !important; }
                `}</style>

                    {/* Right side navbar items */}
                    <ul className="navbar-nav flex-row ms-auto align-items-center gap-1">
                      {!showSidebar && (
                        <>
                          {hasPermission(PERMISSIONS.BANK) && (
                            <li className="nav-item">
                              <a
                                href="#"
                                className="menu-link nav-link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate("/dashboard");
                                }}
                              >
                                Dashboard
                              </a>
                            </li>
                          )}

                          {showWalkins && (
                            <li className="nav-item dropdown">
                              <a
                                href="#"
                                className="menu-link nav-link dropdown-toggle"
                                onClick={(e) => e.preventDefault()}
                              >
                                Walk-Ins
                              </a>
                              <div className="dropdown-menu p-2">
                                {hasPermission(
                                  PERMISSIONS.CUSTOMER_ARRIVAL,
                                ) && (
                                  <button
                                    onClick={() => navigate("/customerwalkin")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Form
                                  </button>
                                )}
                                {hasPermission(PERMISSIONS.CUSTOMER_ASSIGN) && (
                                  <button
                                    onClick={() =>
                                      navigate("/customerassigntable")
                                    }
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Customer Assign
                                  </button>
                                )}
                                {hasPermission(PERMISSIONS.CUSTOMER) && (
                                  <button
                                    onClick={() => navigate("/orderqueue")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Order Queue
                                  </button>
                                )}
                              </div>
                            </li>
                          )}

                          {showOrders && (
                            <li className="nav-item dropdown">
                              <a
                                href="#"
                                className="menu-link nav-link dropdown-toggle"
                                onClick={(e) => e.preventDefault()}
                              >
                                Orders
                              </a>
                              <div className="dropdown-menu p-2">
                                {hasPermission(PERMISSIONS.SALES) && (
                                  <button
                                    onClick={() => navigate("/purchasetable")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Purchase
                                  </button>
                                )}
                                {hasPermission(PERMISSIONS.SALES) && (
                                  <button
                                    onClick={() =>
                                      navigate("/metalpurchasetable")
                                    }
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Old Metal Purchase
                                  </button>
                                )}
                                {hasPermission(PERMISSIONS.STOCK_TABLE) && (
                                  <button
                                    onClick={() => navigate("/metaltransfer")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Metal Transfer
                                  </button>
                                )}
                                {hasPermission(PERMISSIONS.REFINERY) && (
                                  <button
                                    onClick={() => navigate("/refinery")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Refinery
                                  </button>
                                )}
                                {hasPermission(PERMISSIONS.INVOICE) && (
                                  <button
                                    onClick={() => navigate("/invoice")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Sales
                                  </button>
                                )}
                                {hasPermission(
                                  PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE,
                                ) && (
                                  <button
                                    onClick={() =>
                                      navigate("/customorderentrytable")
                                    }
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Custom Order
                                  </button>
                                )}
                                {hasPermission(
                                  PERMISSIONS.CUSTOM_ORDER_ENTRY,
                                ) && (
                                  <button
                                    onClick={() => navigate("/repairingorder")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Repairing Order
                                  </button>
                                )}
                                {hasPermission(
                                  PERMISSIONS.CUSTOM_ORDER_ENTRY,
                                ) && (
                                  <button
                                    onClick={() => navigate("/Jangad")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Jangad
                                  </button>
                                )}
                              </div>
                            </li>
                          )}

                          {hasPermission(PERMISSIONS.STOCK_TABLE) && (
                            <li className="nav-item">
                              <a
                                href="#"
                                className="menu-link nav-link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate("/stocktable");
                                }}
                              >
                                Products
                              </a>
                            </li>
                          )}

                          {showParty && (
                            <li className="nav-item dropdown">
                              <a
                                href="#"
                                className="menu-link nav-link dropdown-toggle"
                                onClick={(e) => e.preventDefault()}
                              >
                                Party
                              </a>
                              <div className="dropdown-menu p-2">
                                {hasPermission(PERMISSIONS.CUSTOMER) && (
                                  <button
                                    onClick={() => navigate("/customertable")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Customer
                                  </button>
                                )}
                                {hasPermission(PERMISSIONS.SUPPLIER) && (
                                  <button
                                    onClick={() => navigate("/karigar")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Supplier/Karigar
                                  </button>
                                )}
                              </div>
                            </li>
                          )}

                          {showItemGroups && (
                            <li className="nav-item dropdown">
                              <a
                                href="#"
                                className="menu-link nav-link dropdown-toggle"
                                onClick={(e) => e.preventDefault()}
                              >
                                Item groups
                              </a>
                              <div className="dropdown-menu p-2">
                                {hasPermission(PERMISSIONS.METAL_GROUP) && (
                                  <button
                                    onClick={() => navigate("/metalgrouptable")}
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Metal Group
                                  </button>
                                )}
                                {hasPermission(PERMISSIONS.ITEM_CATEGORY) && (
                                  <button
                                    onClick={() =>
                                      navigate("/itemcategorytable")
                                    }
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Item Category
                                  </button>
                                )}
                                {hasPermission(PERMISSIONS.PRODUCT_GROUP) && (
                                  <button
                                    onClick={() =>
                                      navigate("/productgrouptable")
                                    }
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Product Group
                                  </button>
                                )}
                                {(hasPermission(
                                  PERMISSIONS.PRODUCT_ASSORTING,
                                ) ||
                                  hasPermission(PERMISSIONS.PRODUCT_GROUP)) && (
                                  <button
                                    onClick={() =>
                                      navigate("/productassortingtable")
                                    }
                                    className="dropdown-item p-2 h6 rounded-1 border-0 bg-transparent text-start"
                                  >
                                    Product Assorting
                                  </button>
                                )}
                              </div>
                            </li>
                          )}

                          {hasPermission(PERMISSIONS.BILL_TYPE) && (
                            <li className="nav-item">
                              <a
                                href="#"
                                className="menu-link nav-link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate("/billtypetable");
                                }}
                              >
                                Bill type
                              </a>
                            </li>
                          )}
                          {hasPermission(PERMISSIONS.DAILY_PRICE) && (
                            <li className="nav-item">
                              <a
                                href="#"
                                className="menu-link nav-link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate("/dailyprice");
                                }}
                              >
                                Daily Price
                              </a>
                            </li>
                          )}
                          {hasPermission(PERMISSIONS.BANK) && (
                            <li className="nav-item">
                              <a
                                href="#"
                                className="menu-link nav-link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate("/bankdetailstable");
                                }}
                              >
                                Bank
                              </a>
                            </li>
                          )}
                          {hasPermission(PERMISSIONS.EMPLOYEE) && (
                            <li className="nav-item">
                              <a
                                href="#"
                                className="menu-link nav-link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate("/employee");
                                }}
                              >
                                Employee
                              </a>
                            </li>
                          )}
                        </>
                      )}
                      <li className="nav-item dropdown">
                        <a
                          href="#"
                          onClick={(e) => e.preventDefault()}
                          className="nav-link nav-icon-hover-bg rounded-circle d-flex d-lg-none align-items-center justify-content-center"
                          type="button"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#mobilenavbar"
                          aria-controls="offcanvasWithBothOptions"
                        >
                          <iconify-icon
                            icon="solar:sort-line-duotone"
                            className="fs-6"
                          />
                        </a>
                      </li>
                      {/* Removed search and notification dropdown */}
                      {showActionControls && (
                        <li className="nav-item dropdown d-flex align-items-center">
                          <button
                            className="nav-link d-flex align-items-center gap-1 px-2"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            disabled={
                              isFiscalYearLoading || fiscalYears.length === 0
                            }
                            title="Financial Year"
                            style={{
                              border: "none",
                              background: "transparent",
                              boxShadow: "none",
                            }}
                          >
                            <span>
                              {formatFiscalYearShort(selectedFiscalYear || "")}
                            </span>
                            <iconify-icon
                              icon="solar:alt-arrow-down-bold"
                              className="fs-2"
                            />
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            {isFiscalYearLoading ? (
                              <li>
                                <span className="dropdown-item-text">
                                  Loading FY...
                                </span>
                              </li>
                            ) : fiscalYears.length > 0 ? (
                              fiscalYears.map((year) => (
                                <li key={year}>
                                  <button
                                    type="button"
                                    className={`dropdown-item ${selectedFiscalYear === year ? "active text-white" : ""}`}
                                    onClick={() => handleFiscalYearChange(year)}
                                  >
                                    {year}
                                  </button>
                                </li>
                              ))
                            ) : (
                              <li>
                                <span className="dropdown-item-text">
                                  No FY
                                </span>
                              </li>
                            )}
                          </ul>
                        </li>
                      )}
                      {/* ------------------------------- */}
                      {/* end Theme Selector */}
                      {/* ------------------------------- */}
                      {/* ------------------------------- */}
                      {/* start lock button */}
                      {/* ------------------------------- */}
                      {showActionControls && (
                        <li className="nav-item nav-icon-hover-bg rounded-circle">
                          <button
                            className="nav-link"
                            onClick={handleManualLock}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: "8px",
                            }}
                            title="Lock Screen"
                          >
                            <iconify-icon
                              icon="solar:lock-keyhole-line-duotone"
                              className="fs-6"
                            />
                          </button>
                        </li>
                      )}
                      {/* ------------------------------- */}
                      {/* end lock button */}
                      {/* ------------------------------- */}
                      {/* ------------------------------- */}
                      {/* start profile Dropdown */}
                      {/* ------------------------------- */}
                      {showActionControls && (
                        <li className="nav-item dropdown">
                          <a
                            className="nav-link"
                            href="#"
                            onClick={(e) => e.preventDefault()}
                            id="drop1"
                            aria-expanded="false"
                          >
                            <div className="d-flex align-items-center gap-2 lh-base">
                              {profileImage ? (
                                <img
                                  src={profileImage}
                                  className="rounded-circle"
                                  width={35}
                                  height={35}
                                  alt="profile"
                                  style={{ objectFit: "cover" }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    setProfileImage(null);
                                  }}
                                />
                              ) : (
                                <div
                                  className={`${getAvatarColor(
                                    profileAvatarSeed,
                                  )} text-white rounded-circle d-flex align-items-center justify-content-center`}
                                  style={{
                                    width: "35px",
                                    height: "35px",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {profileInitials}
                                </div>
                              )}
                              <iconify-icon
                                icon="solar:alt-arrow-down-bold"
                                className="fs-2"
                              />
                            </div>
                          </a>
                          <div
                            className="dropdown-menu profile-dropdown dropdown-menu-end dropdown-menu-animate-up"
                            aria-labelledby="drop1"
                          >
                            <div className="position-relative px-4 pt-3 pb-2">
                              <div className="d-flex align-items-center mb-3 pb-3 border-bottom gap-6">
                                {profileImage ? (
                                  <img
                                    src={profileImage}
                                    className="rounded-circle"
                                    width={56}
                                    height={56}
                                    alt="profile"
                                    style={{ objectFit: "cover" }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      setProfileImage(null);
                                    }}
                                  />
                                ) : (
                                  <div
                                    className={`${getAvatarColor(
                                      profileAvatarSeed,
                                    )} text-white rounded-circle d-flex align-items-center justify-content-center`}
                                    style={{
                                      width: "56px",
                                      height: "56px",
                                      fontSize: "24px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {profileInitials}
                                  </div>
                                )}
                                <div>
                                  <h5 className="mb-0 fs-12">
                                    {profileDisplayLabel}
                                  </h5>
                                  <p className="mb-0 text-dark">
                                    {userData?.phone || userData?.mobile || ""}
                                  </p>
                                </div>
                              </div>
                              <div className="message-body">
                                <button
                                  onClick={() => navigate("/myprofile")}
                                  className="p-2 dropdown-item h6 rounded-1 border-0 bg-transparent w-100 text-start"
                                >
                                  My Profile
                                </button>
                                <ThemeSelector variant="dropdown" />

                                <button
                                  onClick={handleSignOut}
                                  className="p-2 dropdown-item h6 rounded-1 border-0 bg-transparent w-100 text-start"
                                >
                                  Sign Out
                                </button>
                              </div>
                            </div>
                          </div>
                        </li>
                      )}
                      {/* ------------------------------- */}
                      {/* end profile Dropdown */}
                      {/* ------------------------------- */}
                    </ul>
                  </div>
                </div>
              </div>

              {showSidebar &&
                !isDihranModule &&
                currentPinType !== "quote" &&
                (showEmployeeSelfServiceNav || showWhatsAppNav) && (
                  <div className="navbar-toolbar-end flex-shrink-0 ms-lg-2 pe-0 pe-lg-2 order-lg-last gap-2">
                    {showWhatsAppNav && (
                      <button
                        type="button"
                        className="navbar-ess-calendar-btn border-0 bg-transparent p-0"
                        title="WhatsApp"
                        aria-label="WhatsApp"
                        aria-current={isWhatsAppPage ? "page" : undefined}
                        disabled={whatsAppChecking}
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!whatsAppChecking) {
                            await openSetupIfNeeded();
                          }
                        }}
                      >
                        <iconify-icon
                          icon="logos:whatsapp-icon"
                          className="navbar-ess-calendar-icon"
                        />
                      </button>
                    )}
                    {showEmployeeSelfServiceNav && (
                      <Link
                        to="/employeeselfservice"
                        className="navbar-ess-calendar-btn"
                        title="Employee Activity Calendar"
                        aria-label="Employee Activity Calendar"
                        aria-current={
                          isEmployeeSelfServicePage ? "page" : undefined
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <iconify-icon
                          icon="solar:calendar-mark-line-duotone"
                          className="navbar-ess-calendar-icon"
                        />
                      </Link>
                    )}
                  </div>
                )}
            </div>
          </nav>
          {/* ---------------------------------- */}
          {/* End Vertical Layout Header */}
          {/* ---------------------------------- */}
          {/* ------------------------------- */}
          {/* apps Dropdown in Small screen */}
          {/* ------------------------------- */}
          {/*  Mobilenavbar */}
          <div
            className="offcanvas offcanvas-start pt-0"
            data-bs-scroll="true"
            tabIndex={-1}
            id="mobilenavbar"
            aria-labelledby="offcanvasWithBothOptionsLabel"
          >
            <nav className="sidebar-nav scroll-sidebar">
              <div className="offcanvas-header justify-content-between">
                <a href="index.html" className="text-nowrap logo-img">
                  <img src="../assets/images/logos/logo-icon.svg" alt="Logo" />
                </a>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="offcanvas"
                  aria-label="Close"
                />
              </div>
              <div
                className="offcanvas-body pt-0"
                data-simplebar=""
                style={{ height: "calc(100vh - 80px)" }}
              >
                <ul id="sidebarnav" className="list-unstyled">
                  {hasPermission(PERMISSIONS.BANK) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/dashboard")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Dashboard
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.SALES) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/purchasetable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Purchase
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.SALES) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/metalpurchasetable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Old Metal Purchase
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.STOCK_TABLE) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/metaltransfer")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Metal Transfer
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.REFINERY) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/refinery")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Refinery
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.INVOICE) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/invoice")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Sales
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/customorderentrytable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Custom Order
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.CUSTOM_ORDER_ENTRY) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/repairingorder")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Repairing Order
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.CUSTOM_ORDER_ENTRY) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/Jangad")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Jangad
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.STOCK_TABLE) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/stocktable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Products
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.CUSTOMER) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/customertable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Customer
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.SUPPLIER) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/karigar")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Supplier/Karigar
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.METAL_GROUP) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/metalgrouptable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Metal Group
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.ITEM_CATEGORY) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/itemcategorytable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Item Category
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.PRODUCT_GROUP) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/productgrouptable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Product Group
                      </button>
                    </li>
                  )}
                  {(hasPermission(PERMISSIONS.PRODUCT_ASSORTING) ||
                    hasPermission(PERMISSIONS.PRODUCT_GROUP)) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/productassortingtable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Product Assorting
                      </button>
                    </li>
                  )}
                  {hasPermission(PERMISSIONS.CUSTOMER_ASSIGN) && (
                    <li className="sidebar-item py-2">
                      <button
                        onClick={() => navigate("/customerassigntable")}
                        className="d-flex align-items-center w-100 p-2 rounded-1 border-0 bg-transparent text-start"
                      >
                        Customer Assign
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </nav>
          </div>
        </div>
        <div className="app-header with-horizontal">
          <nav
            className="navbar navbar-expand-xl container-fluid p-0"
            style={{ minHeight: "60px" }}
          >
            <ul className="navbar-nav align-items-center">
              <li className="nav-item d-flex d-xl-none">
                <a
                  className="nav-link sidebartoggler nav-icon-hover-bg rounded-circle"
                  id="sidebarCollapse"
                  href="#"
                  onClick={(e) => e.preventDefault()}
                >
                  <iconify-icon
                    icon="solar:hamburger-menu-line-duotone"
                    className="fs-7"
                  />
                </a>
              </li>
              <li className="nav-item d-none d-xl-flex align-items-center">
                <a
                  href="../horizontal/index.html"
                  className="text-nowrap nav-link"
                >
                  <img
                    src="/assets/images/logos/logo_1.png"
                    alt="matdash-img"
                  />
                </a>
              </li>
            </ul>
            <div className="d-block d-xl-none">
              <a href="index.html" className="text-nowrap nav-link">
                <img src="/assets/images/logos/logo_1.png" alt="matdash-img" />
              </a>
            </div>
            <a
              className="navbar-toggler nav-icon-hover p-0 border-0 nav-icon-hover-bg rounded-circle"
              href="#"
              onClick={(e) => e.preventDefault()}
              data-bs-toggle="collapse"
              data-bs-target="#navbarNav"
              aria-controls="navbarNav"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="p-2">
                <i className="ti ti-dots fs-7" />
              </span>
            </a>
            <div
              className="collapse navbar-collapse justify-content-end"
              id="navbarNav"
            >
              <div className="d-flex align-items-center justify-content-between px-0 px-xl-8">
                <ul className="navbar-nav flex-row mx-auto ms-lg-auto align-items-center justify-content-center">
                  <li className="nav-item dropdown">
                    <a
                      href="#"
                      onClick={(e) => e.preventDefault()}
                      className="nav-link nav-icon-hover-bg rounded-circle d-flex d-lg-none align-items-center justify-content-center"
                      type="button"
                      data-bs-toggle="offcanvas"
                      data-bs-target="#mobilenavbar"
                      aria-controls="offcanvasWithBothOptions"
                    >
                      <iconify-icon
                        icon="solar:sort-line-duotone"
                        className="fs-6"
                      />
                    </a>
                  </li>
                  {/* Removed search, mega menu, theme toggle, and notification dropdown */}
                  {/* ------------------------------- */}
                  {/* start lock button */}
                  {/* ------------------------------- */}
                  {showActionControls && (
                    <li className="nav-item nav-icon-hover-bg rounded-circle">
                      <button
                        className="nav-link"
                        onClick={handleManualLock}
                        style={{
                          border: "none",
                          background: "transparent",
                          padding: "8px",
                        }}
                        title="Lock Screen"
                      >
                        <iconify-icon
                          icon="solar:lock-keyhole-line-duotone"
                          className="fs-6"
                        />
                      </button>
                    </li>
                  )}
                  {/* ------------------------------- */}
                  {/* end lock button */}
                  {/* ------------------------------- */}
                  {/* ------------------------------- */}
                  {/* start profile Dropdown */}
                  {/* ------------------------------- */}
                  {showActionControls && (
                    <li className="nav-item dropdown">
                      <a
                        className="nav-link"
                        href="#"
                        onClick={(e) => e.preventDefault()}
                        id="drop1"
                        aria-expanded="false"
                      >
                        <div className="d-flex align-items-center gap-2 lh-base">
                          {profileImage ? (
                            <img
                              src={profileImage}
                              className="rounded-circle"
                              width={35}
                              height={35}
                              alt="profile"
                              style={{ objectFit: "cover" }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                setProfileImage(null);
                              }}
                            />
                          ) : (
                            <div
                              className={`${getAvatarColor(
                                profileAvatarSeed,
                              )} text-white rounded-circle d-flex align-items-center justify-content-center`}
                              style={{
                                width: "35px",
                                height: "35px",
                                fontSize: "14px",
                                fontWeight: "bold",
                              }}
                            >
                              {profileInitials}
                            </div>
                          )}
                          <iconify-icon
                            icon="solar:alt-arrow-down-bold"
                            className="fs-2"
                          />
                        </div>
                      </a>
                      <div
                        className="dropdown-menu profile-dropdown dropdown-menu-end dropdown-menu-animate-up"
                        aria-labelledby="drop1"
                      >
                        <div className="position-relative px-4 pt-3 pb-2">
                          <div className="d-flex align-items-center mb-3 pb-3 border-bottom gap-6">
                            {profileImage ? (
                              <img
                                src={profileImage}
                                className="rounded-circle"
                                width={56}
                                height={56}
                                alt="profile"
                                style={{ objectFit: "cover" }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  setProfileImage(null);
                                }}
                              />
                            ) : (
                              <div
                                className={`${getAvatarColor(
                                  profileAvatarSeed,
                                )} text-white rounded-circle d-flex align-items-center justify-content-center`}
                                style={{
                                  width: "56px",
                                  height: "56px",
                                  fontSize: "24px",
                                  fontWeight: "bold",
                                }}
                              >
                                {profileInitials}
                              </div>
                            )}
                            <div>
                              <h5 className="mb-0 fs-12">
                                {profileDisplayLabel}
                              </h5>
                              <p className="mb-0 text-dark">
                                {userData?.phone || userData?.mobile || ""}
                              </p>
                            </div>
                          </div>
                          <div className="message-body">
                            <button
                              onClick={() => navigate("/myprofile")}
                              className="p-2 dropdown-item h6 rounded-1 border-0 bg-transparent w-100 text-start"
                            >
                              My Profile
                            </button>
                            <ThemeSelector variant="dropdown" />

                            <button
                              onClick={handleSignOut}
                              className="p-2 dropdown-item h6 rounded-1 border-0 bg-transparent w-100 text-start"
                            >
                              Sign Out
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  )}
                  {/* ------------------------------- */}
                  {/* end profile Dropdown */}
                  {/* ------------------------------- */}
                </ul>
              </div>
            </div>
          </nav>
        </div>
      </header>

      <CashbookQuickPopup
        show={showCashPopup}
        onClose={closeCashPopup}
        cashFlowType={cashFlowType}
      />

      <SignOutConfirmModal
        show={showSignOutModal}
        onHide={() => setShowSignOutModal(false)}
        onConfirm={confirmSignOut}
      />

      <WhatsAppSetupModal
        show={whatsAppSetupOpen}
        initialSettings={whatsAppInitialSettings}
        onHide={handleWhatsAppSetupCancel}
        onSuccess={handleWhatsAppSetupSuccess}
      />
    </div>
  );
}

export default Navbar;
